import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import { IApiResponseError, IApiState } from '../../../types/api';
import { RootState } from '../..';
import {
  getKybDetailsAsync,
  IGetKybDetailsResponse,
} from '../kyb/getKybDetailsSlice';

interface IGetDocumentRequestResponse {
  status?: boolean;
  message?: string;
  data?: {
    id?: string;
    businessId?: string;
    kybId?: string;
    userId?: string;
    names?: string[];
    submitted?: boolean;
    submittedAt?: string;
    createdAt?: string;
    updatedAt?: string;
    reason?: string;
  };
  errors?: IApiResponseError[];
}

const initialState: IApiState<IGetDocumentRequestResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const getDocumentRequestAsync = createAsyncThunk(
  'kyb/documentRequest',
  async (_, { rejectWithValue, dispatch, getState }) => {
    try {
      const state = getState() as RootState;

      const accessToken = state.auth.value?.data?.token?.accessToken;

      const businessId = localStorage.getItem('businessId');

      const config = {
        headers: {
          Authorization: `Token ${accessToken}`,
          'X-Business-Id': businessId,
        },
      };

      let data = null;

      if (businessId) {
        const kybDetails = await dispatch(getKybDetailsAsync());

        if (kybDetails.meta.requestStatus === 'fulfilled') {
          const kybDetailsPayload =
            kybDetails.payload as IGetKybDetailsResponse;

          const kybId = kybDetailsPayload.data?.id ?? '';

          const response = await api.get(
            `kyb/${kybId}/document-request`,
            config
          );

          data = response.data;
        }
      }

      return data;
    } catch (err) {
      const error = err as AxiosError<IApiResponseError>;

      return rejectWithValue(error.response?.data);
    }
  }
);

export const documentRequestGetSlice = createSlice({
  name: 'kyb/documentRequest/get',
  initialState,
  reducers: {
    resetGetDocumentRequest: (state) => {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDocumentRequestAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(getDocumentRequestAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as IGetDocumentRequestResponse;
      })
      .addCase(getDocumentRequestAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IGetDocumentRequestResponse;
        state.error = action.error.message;
      });
  },
});

export const { resetGetDocumentRequest } = documentRequestGetSlice.actions;

export default documentRequestGetSlice.reducer;
